<template>
  <div
    id="scroll-target"
    :style="{ 'max-height': `${blockHeight}px` }"
    class="scroll-y"
  >
    <v-layout
      v-scroll:#scroll-target="onScroll"
      style="height: auto"
    >
      <v-card v-if="entity">
        <v-card-text>
          <v-form
            v-model="isValid"
            @input="handleEntityChange"
          >
            <v-layout wrap>
              <v-flex xs12>
                <h2 class="ml-2">
                  Основные данные по Поставке
                </h2>
              </v-flex>
              <v-flex xs3>
                <app-reference
                  v-model="entity.Client"
                  class="ma-2"
                  label="Клиент*"
                  :disabled="authorIsClient"
                  :rules="[(v) => !!v || '']"
                  resource="clients"
                  component-name="Client"
                  :outline="true"
                  :height-field="44"
                  :scroll-value="scrollValue"
                  @input="clientChanged"
                />
              </v-flex>
              <v-flex xs3>
                <ManagerSelect
                  v-model="entity.Manager"
                  label="Менеджер*"
                  :rules="[(v) => !!v || '']"
                  :disabled="!entity.Client"
                  class="mx-2 my-2"
                  :scroll-value="scrollValue"
                  @input="managerChanged"
                />
              </v-flex>
              <v-flex xs3>
                <v-text-field
                  v-model="entity.InternalRequestNumber"
                  class="ma-2"
                  label="Внутренний номер заявки у клиента"
                  hide-details
                  outline
                  height="44"
                  @input="handleEntityChange"
                />
              </v-flex>
              <v-flex xs3>
                <v-text-field
                  v-model="entity.ProductDescription"
                  class="ma-2"
                  label="Описание товара"
                  hide-details
                  outline
                  height="44"
                  @input="handleEntityChange"
                />
              </v-flex>
              <!-- Продавец -->
              <v-flex xs12>
                <h2 class="ml-2 mt-3">
                  Первоначальное местонахождение груза
                </h2>
              </v-flex>
              <v-flex xs12>
                <app-reference
                  v-model="entity.SenderFactory"
                  :outline="true"
                  class="ma-2"
                  label="Продавец*"
                  resource="factories"
                  component-name="Factory"
                  parent-filter="countryId"
                  :parent-id="entity.Country ? entity.Country.id : 0"
                  :without-parent-lock="true"
                  :height-field="44"
                  :rules="[(v) => !!v || '']"
                  :scroll-value="scrollValue"
                  :disabled="isFirstPhaseDone"
                  @handleEntityChange="handleEntityChange"
                />
              </v-flex>
              <v-flex xs12>
                <v-radio-group
                  v-model="senderType"
                  hide-details
                  row
                  height="25"
                  class="my-3 mx-3"
                  :disabled="isFirstPhaseDone"
                  @change="senderTypeChanged"
                >
                  <h2 class="subheading mr-2">
                    Место отгрузки:
                  </h2>
                  <v-radio
                    v-for="(item, value) in senderTypeItems"
                    :key="value"
                    :label="item.text"
                    :value="item.value"
                  />
                </v-radio-group>
              </v-flex>
              <v-flex xs12>
                <SenderFactory
                  v-if="senderType === unloadingPlaceTypes.FACTORY"
                  :entity="entity"
                  :sender-factory="entity.SenderFactory"
                  :loading="entity.LoadingPlace ? true : false"
                  :scroll-value="scrollValue"
                  :changed-sender-type="isChangedSenderType"
                  :disabled="isFirstPhaseDone"
                  :change-info="true"
                  :is-highlight-label="true"
                  @handleEntityChange="handleEntityChange"
                />

                <SenderWarehouse
                  v-if="senderType === unloadingPlaceTypes.GROUPEDWAREHOUSE"
                  :entity="entity"
                  :scroll-value="scrollValue"
                  warehouse-mode="groupedWarehouse"
                  :changed-sender-type="isChangedSenderType"
                  :disabled="isFirstPhaseDone"
                  :change-info="true"
                  :is-highlight-label="true"
                  @handleEntityChange="handleEntityChange"
                />

                <SenderAddress
                  v-if="senderType === unloadingPlaceTypes.ADDRESS"
                  :entity="entity"
                  :scroll-value="scrollValue"
                  :changed-sender-type="isChangedSenderType"
                  :disabled="isFirstPhaseDone"
                  :change-info="true"
                  :is-highlight-label="true"
                  @handleEntityChange="handleEntityChange"
                />

                <SenderWarehouse
                  v-if="senderType === unloadingPlaceTypes.OTHERWAREHOUSE"
                  :entity="entity"
                  :scroll-value="scrollValue"
                  warehouse-mode="otherWarehouse"
                  :changed-sender-type="isChangedSenderType"
                  :disabled="isFirstPhaseDone"
                  :is-highlight-label="true"
                  :change-info="true"
                  @handleEntityChange="handleEntityChange"
                />
              </v-flex>
              <v-flex xs12>
                <h2 class="ml-2 mt-3">
                  Характеристики товара
                </h2>
              </v-flex>
              <v-flex xs4>
                <v-text-field
                  v-model="entity.CommentForShipping"
                  class="ma-2"
                  label="Комментарии к поставке"
                  hide-details
                  outline
                  height="44"
                  @input="handleEntityChange"
                />
              </v-flex>
              <v-flex xs4>
                <DecimalEditor
                  v-model="entity.Weight"
                  class="ma-2"
                  label="Вес брутто, кг*"
                  hide-details
                  :scale="3"
                  :rules="isNumberRule"
                  :outline="true"
                  :height-field="44"
                  @input="handleEntityChange"
                />
              </v-flex>
              <v-flex xs4>
                <v-tooltip
                  v-if="warehouseKievVolume"
                  top
                >
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <DecimalEditor
                        v-model="entity.Volume"
                        class="ma-2"
                        label="Объем, м3*"
                        :scale="3"
                        :rules="isNumberRule"
                        :outline="true"
                        :height-field="44"
                        :available-zero="true"
                        @input="handleEntityChange"
                      />
                    </span>
                  </template>
                  <span>Объем проставлен из ERP</span>
                </v-tooltip>
                <DecimalEditor
                  v-else
                  v-model="entity.Volume"
                  class="ma-2"
                  label="Объем, м3*"
                  :scale="3"
                  :rules="isNumberRule"
                  :outline="true"
                  :height-field="44"
                  :available-zero="true"
                  @input="handleEntityChange"
                />
              </v-flex>
              <v-flex xs4>
                <DecimalEditor
                  v-model="entity.InvoiceSum"
                  class="ma-2"
                  label="Стоимость партии груза*"
                  :scale="2"
                  :rules="isEmptyRule"
                  :outline="true"
                  :height-field="44"
                  @input="handleEntityChange"
                />
              </v-flex>
              <v-flex xs4>
                <app-reference
                  v-model="entity.Currency"
                  class="ma-2"
                  label="Валюта*"
                  :rules="[(v) => !!v || '']"
                  resource="currencies"
                  component-name="Currency"
                  :outline="true"
                  :height-field="44"
                  :scroll-value="scrollValue"
                  @input="handleEntityChange"
                />
              </v-flex>
              <v-flex xs4>
                <v-btn
                  small
                  class="mt-3"
                  @click="expandedProductInfo = !expandedProductInfo"
                >
                  {{
                    !expandedProductInfo
                      ? "Доп инфо по товару"
                      : "Скрыть доп.поля"
                  }}
                </v-btn>
              </v-flex>
              <!-- Доп инфо о товаре -->
              <v-flex xs12>
                <v-layout
                  v-show="expandedProductInfo"
                  column
                  class="list-background"
                >
                  <Packages
                    v-model="entity.Packages"
                    :scroll-value="scrollValue"
                    :is-shipping="true"
                    @change="changePackages"
                  />
                  <v-text-field
                    v-model="entity.CargoComment"
                    class="ma-2"
                    label="Комментарии к размерам и кол-ву грузовых мест"
                    hide-details
                    outline
                    height="44"
                    @input="handleEntityChange"
                  />
                </v-layout>
              </v-flex>
              <v-flex xs12>
                <h2 class="ml-2 mt-3">
                  Адрес доставки товара
                </h2>
              </v-flex>
              <v-flex xs4>
                <EnumSelect
                  v-model="entity.ShippingType"
                  class="ma-2"
                  label="Тип поставки*"
                  resource="delivery-types"
                  :rules="[(v) => !!v || v == 0 || '']"
                  :return-object="true"
                  :outline="true"
                  :height-field="44"
                  :scroll-value="scrollValue"
                  @input="shippingTypeChanged"
                />
              </v-flex>
              <v-flex xs4>
                <app-reference
                  v-model="entity.DestinationCountry"
                  class="ma-2"
                  label="Страна получателя по докам фабрики/отправителя*"
                  :rules="[(v) => !!v || '']"
                  resource="countries"
                  component-name="Country"
                  :outline="true"
                  :height-field="44"
                  :scroll-value="scrollValue"
                  @input="handleEntityChange"
                />
              </v-flex>
              <v-flex xs4>
                <CountryFilteringReference
                  v-model="entity.UnloadingCountry"
                  :rules="[(v) => !!v || '']"
                  :height-field="44"
                  :outline="true"
                  :disabled="isDisabledUnloadingCountry"
                  :without-ukraine="countryWithoutUkraine"
                  :clearable="true"
                  label="Страна выгрузки*"
                  resource="countries"
                  component-name="Country"
                  class="ma-2"
                  :scroll-value="scrollValue"
                  @input="unloadingCountryChanged"
                />
              </v-flex>
              <v-flex xs6>
                <app-reference
                  v-model="entity.UnloadingCity"
                  class="ma-2"
                  label="Город выгрузки*"
                  :rules="[(v) => !!v || '']"
                  resource="cities"
                  component-name="City"
                  parent-filter="countryId"
                  :parent-id="
                    entity.UnloadingCountry ? entity.UnloadingCountry.id : 0
                  "
                  :outline="true"
                  :height-field="44"
                  :scroll-value="scrollValue"
                />
              </v-flex>
              <v-flex xs6>
                <app-reference
                  v-model="entity.UnloadingAddress"
                  class="ma-2"
                  label="Адрес выгрузки*"
                  :rules="[(v) => !!v || '']"
                  resource="addresses"
                  component-name="Address"
                  parent-filter="cityId"
                  :parent-id="
                    entity.UnloadingCity ? entity.UnloadingCity.id : 0
                  "
                  :outline="true"
                  :height-field="44"
                  :scroll-value="scrollValue"
                  @input="unloadingAddressChanged"
                />
              </v-flex>

              <UnloadingContacts
                :value="entity.ContactsUnloading"
                :scroll-value="scrollValue"
                :entity-id="entity.Id"
                :is-our-warehouse="isOurWarehouse"
                @change="contactsUnloadingChanged"
              />
            </v-layout>
            <v-btn
              small
              color="primary"
              :disabled="!(isValid && isChanged)"
              :loading="loading"
              @click="submit()"
            >
              Сохранить
            </v-btn>
            <v-btn
              small
              @click="cancel"
            >
              Отмена
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-layout>
  </div>
</template>

<script>
import mixin from "../../../../mixins/confirm-cancel-changes.js";
import auth from "../../../../auth";
import * as shippingsApi from "../../../../backend/shippingsApi.js";
import { getUkraine } from "../../../../backend/countriesApi.js";
import SenderFactory from "../new-shipping/SenderFactory.vue";
import SenderWarehouse from "../new-shipping/SenderWarehouse.vue";
import SenderAddress from "../new-shipping/SenderAddress.vue";
import { shippingTypes } from "../../shipping-types.js";
import CountryFilteringReference from "../../../../views/countries/CountryFilteringReference.vue";
import * as clientsApi from "../../../../backend/clientsApi.js";
import { unloadingPlaceTypes } from "../../../../enums/unloading-place-types.js";
import { phaseStates } from "../phases/shipping-phase-states.js";
import UnloadingContacts from "../new-shipping/UnloadingContacts.vue";
import Packages from "../new-shipping/Packages.vue";

export default {
  $_veeValidate: {
    validator: "new",
  },
  components: {
    SenderFactory,
    SenderWarehouse,
    SenderAddress,
    CountryFilteringReference,
    UnloadingContacts,
    Packages,
  },
  mixins: [mixin],
  props: ["id"],
  data() {
    return {
      isValid: false,
      isChanged: false,
      entity: null,
      originEntity: null,
      expanded: false,
      clientLoading: false,
      senderType: null,
      dataRestored: false,
      isEmptyRule: [(v) => !!v || "Поле обязательно к заполнению"],
      isNumberRule: [
        (v) => !!v || "Поле обязательно к заполнению",
        (v) =>
          !isNaN(v.replace(/\s/g, "").replace(",", ".")) ||
          "Некорректное значение",
      ],
      expandedProductInfo: false,
      isOurWarehouse: false,
      senders: [],
      newClientAlias: "",
      deliveryTypes: [],
      ukraine: null,
      error: false,
      wantedDates: null,
      scrollValue: 0,
      isChangedSenderType: false,
      contactUnloading: null,
      contactUnloadingPhone: "",
      loading: false,
    };
  },
  computed: {
    authorIsClient() {
      return auth.getUser().isClient || !!this.entity.AuthorContactPerson?.id;
    },
    isAfterReadyDate() {
      if (this.wantedDates && this.wantedDates.dateFrom) {
        return this.moment(this.wantedDates.dateFrom).isAfter(
          this.entity.ReadyDate
        );
      }
    },
    senderTypeItems() {
      return this.$store.getters["shipping/senderTypeItems"];
    },
    blockHeight() {
      return window.innerHeight - window.innerHeight * 0.1;
    },
    isDisabledUnloadingCountry() {
      if (!this.entity.ShippingType) {
        return false;
      }
      return (
        (this.entity.ShippingType.id === shippingTypes.ImportUkraine ||
          this.entity.ShippingType.id === shippingTypes.InsideUkraine) &&
        this.entity.UnloadingCountry !== null
      );
    },
    countryWithoutUkraine() {
      return (
        this.entity.ShippingType &&
        (this.entity.ShippingType.id === shippingTypes.ExportUkraine ||
          this.entity.ShippingType.id === shippingTypes.OutsideUkraine)
      );
    },
    unloadingPlaceTypes() {
      return unloadingPlaceTypes;
    },
    isFirstPhaseDone() {
      const phases = this.$store.getters["shipping/phases"];
      if (!phases.length) {
        return false;
      }
      return !!phases.find((p) => p.state.id === phaseStates.DONE);
    },
    warehouseKievVolume() {
      return this.$store.getters["shipping/warehouseKievVolume"];
    },
  },
  created() {
    this.getData();
  },
  methods: {
    onScroll(e) {
      this.scrollValue = e.target.scrollTop;
    },
    async getData() {
      const res = await shippingsApi.getShipping(this.id);
      const newEntity = {};
      for (const key in res.data) {
        if (key === "WantedDates") {
          newEntity[key] = res.data[key].map((d) => {
            return {
              range: d.Range,
            };
          });
        } else if (key === "SenderWarehouse" && res.data[key]) {
          newEntity[key] = {
            id: res.data[key].Id,
            title: res.data[key].Title,
            isRemoved: res.data[key].IsRemoved,
            groupedWarehouse: res.data[key].GroupedWarehouse,
          };
        } else if (key == "ContactsLoading" || key == "ContactsUnloading") {
          newEntity[key] = res.data[key].length
            ? res.data[key].map((item) => {
              const contact = {};
              for (const prop in item) {
                if (typeof item[prop] === "object") {
                  contact[`${prop.charAt(0).toLowerCase()}${prop.slice(1)}`] =
                      item[prop]
                        ? {
                          id: item[prop].Id,
                          title: item[prop].Title,
                        }
                        : null;
                } else {
                  contact[`${prop.charAt(0).toLowerCase()}${prop.slice(1)}`] =
                      item[prop];
                }
              }
              return contact;
            })
            : [];
        } else if (key === "Packages") {
          newEntity[key] =
            res.data[key] && res.data[key].length
              ? res.data[key].map((p) => {
                return {
                  id: p.Id,
                  packageType: {
                    id: p.PackageType.Id,
                    title: p.PackageType.Title,
                  },
                  quantity: p.Quantity,
                  length: p.Length,
                  width: p.Width,
                  height: p.Height,
                };
              })
              : [];
        } else if (typeof res.data[key] === "object") {
          newEntity[key] = res.data[key]
            ? {
              id: res.data[key].Id,
              title: res.data[key].Title,
              isRemoved: res.data[key].IsRemoved,
            }
            : null;
        } else {
          newEntity[key] = res.data[key];
        }
      }
      this.entity = { ...newEntity };
      if (this.entity.SenderFactory && this.entity.LoadingAddress) {
        const response = await shippingsApi.getFactory(
          this.entity.SenderFactory.id
        );
        const item = response.LoadingPlaces.find(
          (i) => i.Address.Id === this.entity.LoadingAddress?.id
        );
        if (item) {
          this.$set(this.entity, "LoadingPlace", {
            id: item.Id,
            title: item.Address.Title,
          });
        } else {
          this.$set(this.entity, "LoadingPlace", null);
        }
      }

      this.wantedDates = {
        dateFrom: this.entity.WantedDateFrom,
        dateTo:
          this.entity.WantedDateTo !== this.entity.WantedDateFrom
            ? this.entity.WantedDateTo
            : null,
      };
      this.senderType = this.entity.SenderType;
      this.originEntity = JSON.parse(JSON.stringify(this.entity));
      this.unloadingAddressChanged();
      this.ukraine = await getUkraine();
      this.dataRestored = true;
    },
    senderTypeChanged() {
      this.entity.SenderWarehouse = null;
      this.entity.SenderAddress = null;
      if (!this.entity.SenderFactory) {
        this.entity.Country = null;
      }
      this.isChangedSenderType = true;
    },
    async submit() {
      const entityForSaving = {
        authorId: this.entity.Author.id,
        date: this.entity.Date,
        clientId: this.entity.Client.id,
        managerId: this.entity.Manager.id,
        countryId: this.entity.Country.id,
        loadingAddressId: this.entity.LoadingAddress.id,
        contactsLoading:
          this.entity.ContactsLoading && this.entity.ContactsLoading.length
            ? this.entity.ContactsLoading.map((c) => {
              return {
                id: c.id,
                contactPersonId: c.contactPerson.id,
                contactPhone: c.contactPhone,
                contactEmail: c.contactEmail,
              };
            })
            : [],
        destinationCountryId: this.entity.DestinationCountry.id,
        unloadingCountryId: this.entity.UnloadingCountry.id,
        unloadingCityId: this.entity.UnloadingCity.id,
        unloadingAddressId: this.entity.UnloadingAddress.id,
        invoiceNumber: this.entity.InvoiceNumber,
        invoiceSum: this.entity.InvoiceSum,
        currencyId: this.entity.Currency.id,
        senderWarehouseId: this.entity.SenderWarehouse
          ? this.entity.SenderWarehouse.id
          : null,
        senderFactoryId: this.entity.SenderFactory
          ? this.entity.SenderFactory.id
          : null,
        senderAddressId: this.entity.SenderAddress
          ? this.entity.SenderAddress.id
          : null,
        loadingPlaceTimetable: this.entity.LoadingPlaceTimetable,
        deliveryToUnloadingPlaceDate: this.entity.DeliveryToUnloadingPlaceDate,
        volume: this.entity.Volume,
        packages: this.entity.Packages.length
          ? this.entity.Packages.map((p) => {
            return {
              packageTypeId: p.packageType.id,
              quantity: p.quantity,
              length: p.length,
              width: p.width,
              height: p.height,
            };
          })
          : [],
        cargoComment: this.entity.CargoComment,
        weight: this.entity.Weight,
        shippingTypeId: this.entity.ShippingType.id,
        contactsUnloading:
          this.entity.ContactsUnloading && this.entity.ContactsUnloading.length
            ? this.entity.ContactsUnloading.map((c) => {
              return {
                id: c.id,
                contactPersonId: c.contactPerson.id,
                contactPhone: c.contactPhone,
              };
            })
            : [],
        internalRequestNumber: this.entity.InternalRequestNumber,
        clientAlias: this.entity.ClientAlias,
        commentForShipping: this.entity.CommentForShipping,
        loadingNumber: this.entity.LoadingNumber,
        productDescription: this.entity.ProductDescription,
        wantedDateFrom: this.wantedDates.dateFrom,
        wantedDateTo: this.wantedDates.dateTo
          ? this.wantedDates.dateTo
          : this.wantedDates.dateFrom,
        senderTypeId: this.senderType,
      };

      this.loading = true;
      await shippingsApi.updateShipping(this.id, entityForSaving);
      this.loading = false;
      this.$emit("save");
    },
    cancel() {
      this.$emit("cancel");
    },
    handleEntityChange() {
      this.checkValidWantedDate();
      function isEntityReallyChanged(origin, current) {
        if (!origin) {
          return true;
        } else {
          let isChanged = false;
          for (const property in origin) {
            if (typeof current[property] === "object") {
              isChanged =
                current[property] && origin[property]
                  ? origin[property].id !== current[property].id
                  : true;
            } else if (Array.isArray(origin[property])) {
              isChanged =
                JSON.stringify(current[property]) ==
                JSON.stringify(origin[property]);
            } else {
              isChanged =
                origin[property].toString() !== current[property].toString();
            }
            if (isChanged) {
              return true;
            }
          }
        }
        return false;
      }
      if (this.dataRestored) {
        this.isChanged = isEntityReallyChanged(this.originEntity, this.entity);
      }
    },
    async clientChanged() {
      if (!this.entity.Client) {
        this.entity.Employee = null;
        this.entity.ClientAlias = null;
        this.entity.Manager = null;
        return;
      }
      this.clientLoading = true;
      const manager = await clientsApi.getClientManager(this.entity.Client.id);
      this.entity.Manager = manager ? manager : null;
      this.clientLoading = false;
    },
    countryChanged() {
      this.clearSender();
      this.entity.LoadingAddress = null;
    },
    async unloadingAddressChanged() {
      if (!this.entity.UnloadingAddress) {
        return;
      }

      const res = await shippingsApi.getWarehouseReferenceAddress(
        this.entity.UnloadingAddress.id
      );
      this.isOurWarehouse = res.data ? res.data.OurWarehouse : false;
    },
    shippingTypeChanged() {
      this.handleEntityChange();

      if (
        this.entity.ShippingType &&
        (this.entity.ShippingType.id === shippingTypes.ImportUkraine ||
          this.entity.ShippingType.id === shippingTypes.InsideUkraine)
      ) {
        this.$set(this.entity, "UnloadingCountry", {
          id: this.ukraine.Id,
          title: this.ukraine.Title,
        });
      }

      if (
        this.countryWithoutUkraine &&
        this.entity.UnloadingCountry &&
        this.entity.UnloadingCountry.id === this.ukraine.Id
      ) {
        this.$set(this.entity, "UnloadingCountry", null);
      }
    },
    checkValidWantedDate() {
      this.error = this.isAfterReadyDate === true ? false : true;
    },
    unloadingCountryChanged() {
      this.entity.UnloadingCity = null;
      this.entity.UnloadingAddress = null;
    },
    resetLoaders() {
      this.clientLoading = false;
    },
    async managerChanged(value) {
      if (!value) {
        return;
      }
      await clientsApi.assignClientManager(this.entity.Client.id, value.id);
      this.showInfo(`Менеджер клиента ${this.entity.Client.title} изменен`);
      this.handleEntityChange();
    },
    contactsUnloadingChanged(contactDetails) {
      this.entity.ContactsUnloading = [];
      this.$set(this.entity, "ContactsUnloading", contactDetails);
      this.handleEntityChange();
    },
    changePackages(value) {
      this.entity.Packages = value;
      this.handleEntityChange();
    },
  },
};
</script>
